import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function AuthPage() {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role_type: 'client'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [kycStep, setKycStep] = useState(false);
  const [kycData, setKycData] = useState({
    document_type: 'id_card',
    front_image: null,
    back_image: null
  });

  const generatePassword = () => {
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    const symbols = '!@#$%^&*()_+-=[]{}|;:,.<>?';
    const allChars = lowercase + uppercase + numbers + symbols;
    
    let password = '';
    
    // Assurer au moins un caractère de chaque type
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += symbols[Math.floor(Math.random() * symbols.length)];
    
    // Compléter jusqu'à 18 caractères
    for (let i = password.length; i < 18; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }
    
    // Mélanger
    password = password.split('').sort(() => Math.random() - 0.5).join('');
    
    return password;
  };

  const handleGeneratePassword = () => {
    const newPassword = generatePassword();
    setFormData({ ...formData, password: newPassword });
    setShowPassword(true);
  };

  const validatePassword = (password) => {
    if (password.length < 16) {
      return 'Le mot de passe doit contenir au moins 16 caractères';
    }
    if (!/\d/.test(password)) {
      return 'Le mot de passe doit contenir au moins un chiffre';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Le mot de passe doit contenir au moins une majuscule';
    }
    if (!/[a-z]/.test(password)) {
      return 'Le mot de passe doit contenir au moins une minuscule';
    }
    if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password)) {
      return 'Le mot de passe doit contenir au moins un caractère spécial (!@#$%^&*...)';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validation du mot de passe pour l'inscription
    if (!isLogin) {
      const passwordError = validatePassword(formData.password);
      if (passwordError) {
        setError(passwordError);
        setLoading(false);
        return;
      }
    }

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin 
        ? { email: formData.email, password: formData.password }
        : { email: formData.email, password: formData.password, name: formData.name, role_type: formData.role_type };

      const { data } = await axios.post(`${API_URL}${endpoint}`, payload);
      
      // Sauvegarder le token et les infos utilisateur
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      // Si inscription, proposer KYC
      if (!isLogin) {
        setKycStep(true);
        setLoading(false);
        return;
      }
      
      // Rediriger vers le dashboard
      navigate('/dashboard');
      window.location.reload(); // Force reload pour mettre à jour l'état d'authentification
    } catch (err) {
      setError(err.response?.data?.detail || 'Une erreur est survenue. Veuillez réessayer.');
    } finally {
      if (isLogin) setLoading(false);
    }
  };

  const handleImageUpload = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setKycData(prev => ({
          ...prev,
          [type]: reader.result.split(',')[1] // Get base64 without prefix
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleKYCSubmit = async () => {
    if (!kycData.front_image) {
      alert('Veuillez uploader au moins la photo recto de votre document');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.post(`${API_URL}/kyc/upload`, {
        document_type: kycData.document_type,
        front_image_base64: kycData.front_image,
        back_image_base64: kycData.back_image
      }, config);

      alert('Document KYC uploadé avec succès ! Votre compte sera vérifié sous 48h.');
      navigate('/dashboard');
      window.location.reload();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'upload KYC');
    } finally {
      setLoading(false);
    }
  };

  const skipKYC = () => {
    navigate('/dashboard');
    window.location.reload();
  };

  const handleBackToLanding = () => {
    navigate('/');
  };

  // KYC Step
  if (kycStep) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-2xl">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">🆔 Vérification d'identité (KYC)</h2>
            <p className="text-gray-600">Uploadez votre document d'identité pour vérification</p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type de document
              </label>
              <select
                value={kycData.document_type}
                onChange={(e) => setKycData({ ...kycData, document_type: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="id_card">Carte d'identité</option>
                <option value="passport">Passeport</option>
                <option value="driver_license">Permis de conduire</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Photo Recto *
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, 'front_image')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
                {kycData.front_image && (
                  <p className="text-xs text-green-600 mt-1">✓ Image uploadée</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Photo Verso (optionnel)
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, 'back_image')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
                {kycData.back_image && (
                  <p className="text-xs text-green-600 mt-1">✓ Image uploadée</p>
                )}
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-sm text-blue-900">
                <strong>ℹ️ Information:</strong> Vos documents seront analysés automatiquement par notre système OCR. 
                La vérification admin sera effectuée sous 48h.
              </p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleKYCSubmit}
                disabled={loading}
                className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-300"
              >
                {loading ? 'Upload en cours...' : 'Soumettre KYC'}
              </button>
              <button
                type="button"
                onClick={skipKYC}
                className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50"
              >
                Passer
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center text-blue-900 font-bold text-2xl">🏦</div>
            <div className="text-left">
              <h1 className="text-2xl font-bold text-gray-900">Bank Kreiz Breizh</h1>
              <p className="text-xs text-gray-600">Banque Centrale de Bretagne</p>
            </div>
          </div>
          <button
            onClick={handleBackToLanding}
            className="text-sm text-blue-600 hover:text-blue-700 underline"
          >
            ← Retour à l'accueil
          </button>
        </div>

        {/* Tabs */}
        <div className="flex mb-6 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => {
              setIsLogin(true);
              setError('');
            }}
            className={`flex-1 py-2 px-4 rounded-md transition-all font-semibold ${
              isLogin ? 'bg-white shadow-sm text-blue-600' : 'text-gray-600'
            }`}
          >
            Connexion
          </button>
          <button
            onClick={() => {
              setIsLogin(false);
              setError('');
            }}
            className={`flex-1 py-2 px-4 rounded-md transition-all font-semibold ${
              !isLogin ? 'bg-white shadow-sm text-blue-600' : 'text-gray-600'
            }`}
          >
            Inscription
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom complet *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Jean Dupont"
                required
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="email@exemple.com"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mot de passe *
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="••••••••"
                required
                minLength="16"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 text-gray-600 hover:text-gray-900 text-xl"
                title={showPassword ? "Masquer" : "Afficher"}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
            {!isLogin && (
              <div className="mt-2">
                <button
                  type="button"
                  onClick={handleGeneratePassword}
                  className="text-sm px-3 py-1.5 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-medium hover:from-green-600 hover:to-green-700 transition-all"
                >
                  🎲 Générer un mot de passe sécurisé
                </button>
              </div>
            )}
          </div>

          {!isLogin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type de compte *
              </label>
              <select
                value={formData.role_type}
                onChange={(e) => setFormData({ ...formData, role_type: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="client">Client Particulier</option>
                <option value="enterprise">Entreprise</option>
                <option value="admin">Administrateur</option>
              </select>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              ⚠️ {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Chargement...
              </span>
            ) : (
              isLogin ? 'Se connecter' : "S'inscrire"
            )}
          </button>

          {/* Forgot Password Link */}
          {isLogin && (
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => navigate('/password-recovery')}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                🔑 Mot de passe ou email oublié ?
              </button>
            </div>
          )}
        </form>

        {/* Info */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-sm text-blue-900 font-semibold mb-2">🔒 Connexion sécurisée</p>
            <p className="text-xs text-blue-700">
              Votre connexion est chiffrée de bout en bout. En vous inscrivant, un compte bancaire de 100€ est créé automatiquement avec un IBAN breton (BZ).
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AuthPage;
